import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { Button } from 'src/common/components/button';
import { useModel } from 'src/common/create-model';
import { useBuyGuestCount } from 'src/components/buy-guest-count-modal';
import { useOpenUpgradeAiModal } from 'src/components/select-upgrade-plan/upgrade-ai';
import { useEnableAI } from 'src/hooks/block/use-enable-AI';
import { getCurrentSpace } from 'src/hooks/space';
import { PRO_SPACE_PLAN, useIsProSpace } from 'src/hooks/space/use-is-pro-space';
import { SpacePlanType } from 'src/redux/types';
import { useLimitConfig } from 'src/services/app/hook/subscription-data';
import { useIsAiPlan, useSpaceCapacity } from 'src/services/capacity';
import { useCurrentSpacePlanInfo } from 'src/services/capacity/space-plans';
import { getSpacePlanTypeName } from 'src/utils/block-utils';
import { SettingModalCommon, SettingProvider } from '../common';
import { OpenSettingFrom } from '../type';
import { useOpenUpgradePlan } from '../use-open-upgrade-plan';
import { Icon } from 'src/common/components/icon';

const useUpgradeSettingHeader = () => {
  const { t } = useAppTranslation();
  const currentSpace = getCurrentSpace();
  const { spacePlan } = useCurrentSpacePlanInfo();

  // const { blocksMaxLimit } = useLimitConfig();

  /** 是不是自动续费套餐 */

  const timeFormat = dayjs(spacePlan?.endAt).format('YYYY.MM.DD');

  const content = useMemo(() => {
    if (!currentSpace.planType) return null;

    // 付费
    if (PRO_SPACE_PLAN.includes(currentSpace.planType)) {
      const lastTime = t('views.settings.upgrade.valid_until', { date: timeFormat });
      return <>{lastTime}</>;
    }

    return null;
  }, [currentSpace.planType, t, timeFormat]);
  return { content, currentSpace, timeFormat };
};

export const useOpenUpgradeModal = () => {
  const openUpgradePlan = useOpenUpgradePlan();
  const isProSpace = useIsProSpace();
  const { currentSpace } = useUpgradeSettingHeader();
  const isTeamSpace = currentSpace?.planType === SpacePlanType.team;

  return useCallback(
    (spacePlan?: SpacePlanType) => {
      const plan =
        spacePlan ||
        (isProSpace ? (isTeamSpace ? SpacePlanType.team : SpacePlanType.pro) : SpacePlanType.pro);

      openUpgradePlan(plan);
    },
    [isProSpace, isTeamSpace, openUpgradePlan]
  );
};

// 付费版空间公告
export const UpgradeSettingHeader: FC = () => {
  const { t } = useAppTranslation();
  const { memberMaxLimitDesc } = useLimitConfig();
  const openUpgradeModal = useOpenUpgradeModal();
  const { content, currentSpace } = useUpgradeSettingHeader();
  const isProSpace = useIsProSpace();
  const buyGuestCount = useBuyGuestCount();

  if (!currentSpace) return null;
  return (
    <div className="text-t2 h-10 flex items-center space-x-2 text-grey3 flex-shrink-0">
      <div className="whitespace-nowrap space-x-2">
        <span>{t('common.current_version')}</span>
        <span className="text-t2-bold text-black">
          {getSpacePlanTypeName(currentSpace.planType)}
          {t('views.settings.upgrade.current_space')}
        </span>
        <span>{t('views.settings.upgrade.seats')}</span>
        <span className="text-t2-bold text-black">{memberMaxLimitDesc}</span>
      </div>
      <div className="flex items-center space-x-2">
        <div>{content}</div>
        <Button onClick={() => openUpgradeModal()}>{t('common.buy_more_seats')}</Button>
        {!__BUILD_IN__ && isProSpace && (
          <Button onClick={() => buyGuestCount({ spaceId: currentSpace.uuid })}>
            {t('common.buy_more_guests')}
          </Button>
        )}
      </div>
    </div>
  );
};

const FuAiHeader: FC = () => {
  const { t } = useAppTranslation();
  const spaceCapacity = useSpaceCapacity();
  const openUpgradeAiModal = useOpenUpgradeAiModal();
  const isProSpace = useIsProSpace();
  const { enableAI } = useEnableAI();
  const tips = isProSpace ? '' : t('common.unlimited_times');

  const handleClick = () => {
    openUpgradeAiModal({
      isFrom: OpenSettingFrom.package,
    });
  };

  return (
    <>
      <UpgradeSettingHeader />
      <SettingModalCommon.SettingDivider className="mb-2.5" />
      {enableAI && (
        <>
          <SettingModalCommon.Item
            leftChild={t('common.product_ai_title')}
            rightChild={isProSpace ? t('common.unlimited_times') : ''}
          />
          {!isProSpace && (
            <SettingModalCommon.Item
              leftChild={t('common.ai_usage_status', {
                used: spaceCapacity.usage,
                total: spaceCapacity.aiRestUsage,
              })}
              rightChild={
                isProSpace ? (
                  <></>
                ) : (
                  <Button onClick={handleClick}>
                    {t('views.settings.upgrade.buy_ai_title', {
                      title: t('common.product_ai_pay_title'),
                    })}
                  </Button>
                )
              }
            />
          )}
          {!isProSpace && (
            <SettingModalCommon.Item
              leftChild={t('views.settings.upgrade.buy_ai_benefits', {
                title: t('common.product_ai_pay_title'),
                tips,
              })}
            />
          )}
          <SettingModalCommon.SettingDivider className="mt-2.5 mb-5" />
        </>
      )}
    </>
  );
};

const SpacePlanInfo = () => {
  const { t } = useAppTranslation();
  const openUpgradeModal = useOpenUpgradeModal();
  const isProSpace = useIsProSpace();
  const { currentSpace, timeFormat } = useUpgradeSettingHeader();
  const { spacePlan } = useCurrentSpacePlanInfo();
  const { memberMaxLimitDesc } = useLimitConfig();
  let showLifeTime = false;
  // 时间在2100年之后的一律显示终身
  if (isProSpace && spacePlan?.endAt && spacePlan.endAt > 4104758612000) {
    showLifeTime = true;
  }

  return (
    <>
      <p>{t('views.settings.upgrade.current_space')}</p>
      <p>
        <span className="text-h4 text-black mr-2">
          {getSpacePlanTypeName(currentSpace.planType)}
        </span>
        <div className="mt-1">
          <span>
            {memberMaxLimitDesc} {t('views.settings.upgrade.seats')}
          </span>
          <span className="mx-2 text-active_color animate-click" onClick={() => openUpgradeModal()}>
            {t('common.upgrade')}
          </span>
        </div>
      </p>
      {showLifeTime ? (
        <p>Lifetime</p>
      ) : (
        <p hidden={!isProSpace}>{t('views.settings.upgrade.expires_on', { date: timeFormat })}</p>
      )}
    </>
  );
};

const BuildInAiHeader: FC = () => {
  const { t } = useAppTranslation();
  const spaceCapacity = useSpaceCapacity();
  const isAiPlan = useIsAiPlan();
  const { toUpgradeAi } = useModel(SettingProvider);

  const baseModelRest = isAiPlan
    ? spaceCapacity.baseModel.monthLimit - spaceCapacity.baseModel.monthUsed
    : spaceCapacity.baseModel.dayLimit - spaceCapacity.baseModel.todayUsed;

  const pdfRest = isAiPlan
    ? spaceCapacity.pdf.dayLimit - spaceCapacity.pdf.todayUsed
    : spaceCapacity.pdf.monthLimit - spaceCapacity.pdf.monthUsed;

  return (
    <div className="mt-2.5 mb-5 border flex-shrink-0 overflow-auto text-grey3">
      <div className="min-w-[720px] h-40 grid grid-cols-4">
        <div className="border-r p-2.5 space-y-2">
          <SpacePlanInfo />
        </div>
        {/* <div className="flex items-center justify-between w-80 h-[102px] bg-aside rounded-lg p-4">
          <div className="flex flex-col h-full justify-center w-[150px] text-normal">
            <div className=" flex items-center space-x-2">
              <Icon className="text-ai_color " name={'IcAiFill'} size="middle" />
              <div className="text-t1-medium">{appI18n.t('common.product_ai_title')}</div>
            </div>
            <div className="text-t2 mt-2">Search, generate, and chat with unlimited AI</div>
          </div>
          <Button
            colorType="active"
            onClick={() => {
              toUpgradeAi();
            }}
          >
            Add to plan
          </Button>
        </div> */}
        <div className="col-span-3 p-2.5 w-full flex flex-col">
          <div className="flex justify-between items-center border-b pb-2.5 w-full">
            <div className="space-y-2">
              <p>{t('views.settings.upgrade.ai_benefits_current')}</p>
              <p className="text-h4 text-black">
                {isAiPlan
                  ? t('views.settings.upgrade.professional')
                  : t('views.settings.upgrade.free')}
              </p>
            </div>
            <div className="space-x-2">
              <span>{t('views.settings.upgrade.ai_not_enough')}</span>
              <span className="text-active_color animate-click" onClick={() => toUpgradeAi()}>
                {t('common.buy_query_quota')}
              </span>
            </div>
          </div>
          <div className="pt-2.5 grid grid-cols-4 flex-1">
            {[
              {
                num: spaceCapacity.baseModel.todayUsed,
                title: t('views.settings.upgrade.today_ai_queries'),
              },
              { num: baseModelRest, title: t('views.settings.upgrade.remaining_ai_queries') },
              {
                num: spaceCapacity.pdf.todayUsed,
                title: t('views.settings.upgrade.today_pdf_queries'),
              },
              { num: pdfRest, title: t('views.settings.upgrade.remaining_pdf_queries') },
            ].map((item) => (
              <div
                className="flex flex-col justify-center items-center space-y-2 text-center"
                key={item.title}
              >
                <p>{item.num}</p>
                <p>{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const UpgradeSettingAiHeader = __BUILD_IN__ ? BuildInAiHeader : FuAiHeader;
